import { TenantProcess, TenantProcessStatus } from './tenantProcessStateBase';

// What a surface knows when it starts a process: the rows a scene selected, the job a modal holds (PLAN-070), and for
// an assessment's automation the assessment task and the item it assesses (PLAN-073)
export type CopilotProcessStartInput = {
  entityIds?: number[];
  jobId?: number;
  consumerTaskId?: number;
  entityType?: number;
  entityId?: number;
  // the KPI report's file (KpiReportFormat); left out, the report's card asks for it
  format?: number;
};

export enum CopilotActionType {
  Field = 1,
  Process = 2,
  // A ready-made question: clicking it sends the prompt as if the user had typed it, so the panel
  // starts on the subject instead of on an empty box. No backend process behind it.
  Prompt = 3,
}

/**
 * The panel's view-model of one action of the active usage. It is derived from an `AIActionBinding`
 * (see `actions/aiActionBinding.ts`); the id is the binding id, so a scene and the panel agree on it.
 */
export class CopilotAction {
  id: string;

  name: string;

  type: CopilotActionType;

  processId: TenantProcess | undefined;

  status?: TenantProcessStatus;

  disabled?: boolean;

  selected?: boolean;

  // CopilotActionType.Prompt only: the question that is sent on click
  prompt?: string;

  // The action takes the rows a scene selected: a Prompt is sent as an entity-selection turn, worded by the
  // usage on the selection; a Process start gets them as its start input.
  acceptsSelection?: boolean;

  // CopilotActionType.Prompt only: the general knowledge skill sent with the prompt (CopilotGenericContentDTO.skill)
  skill?: string;

  // CopilotActionType.Prompt only: the turn takes the Library's selected row as its document
  usesSelectedDocument?: boolean;

  /**
   * The linked context: the in-app route this action belongs to, where its result appears. Invoking
   * the action navigates there first, and a scene's AI menu only lists actions that belong to it.
   * An action without a route belongs everywhere its usage is active.
   */
  route?: string;

  // The process keeps running server-side without the panel watching it (the framework audit runs for
  // as long as a whole framework takes). The panel therefore lets the user work on something else
  // while this one is InProgress, instead of holding the panel until it finishes.
  runsInBackground?: boolean;

  // CopilotActionType.Process only: what the requesting surface handed over for this start; the action of a start
  // only, never the listed one
  startInput?: CopilotProcessStartInput;

  // A process the panel follows without the usage listing it (a chat turn started it): never in an action list
  hidden?: boolean;

  constructor() {
    this.id = '';
    this.name = '';
    this.type = CopilotActionType.Field;
    this.status = TenantProcessStatus.NotStarted;
  }

  clone = (): CopilotAction => {
    const newAction = new CopilotAction();
    newAction.id = this.id;
    newAction.name = this.name;
    newAction.disabled = this.disabled;
    newAction.selected = this.selected;
    newAction.status = this.status;
    newAction.type = this.type;
    newAction.processId = this.processId;
    newAction.prompt = this.prompt;
    newAction.acceptsSelection = this.acceptsSelection;
    newAction.skill = this.skill;
    newAction.usesSelectedDocument = this.usesSelectedDocument;
    newAction.route = this.route;
    newAction.runsInBackground = this.runsInBackground;
    newAction.startInput = this.startInput;
    newAction.hidden = this.hidden;

    return newAction;
  };
}
